import * as Sentry from "@sentry/react";
import { getConsentState, onConsentChange } from "./consent";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.MODE as string) || "development";
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

function consentAllowsErrorReporting(): boolean {
  // Sentry is treated as a "preferences" / functional cookie — not analytics.
  // Error reporting is critical to keeping the site working, but we still gate
  // it behind consent in opt-in regions to be safe.
  return getConsentState().preferences === true || getConsentState().analytics === true;
}

export function initSentry(): boolean {
  if (initialized) return true;
  if (!DSN) return false;
  if (ENV === "development") return false;
  if (!consentAllowsErrorReporting()) return false;

  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      release: RELEASE,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      integrations: [],
      beforeBreadcrumb(breadcrumb) {
        try {
          if (breadcrumb.data && typeof breadcrumb.data === "object") {
            const data = breadcrumb.data as Record<string, unknown>;
            for (const key of ["url", "to", "from"]) {
              const val = data[key];
              if (typeof val === "string") {
                data[key] = val.split("?")[0].split("#")[0];
              }
            }
          }
          if (breadcrumb.category === "ui.input") return null;
          if (breadcrumb.category === "console" && typeof breadcrumb.message === "string") {
            breadcrumb.message = breadcrumb.message.slice(0, 200);
          }
        } catch {}
        return breadcrumb;
      },
      beforeSend(event) {
        try {
          if (event.request?.url) {
            event.request.url = event.request.url.split("?")[0].split("#")[0];
          }
        } catch {}
        return event;
      },
    });
    initialized = true;
    return true;
  } catch (err) {
    console.warn("Sentry init failed:", err);
    return false;
  }
}

if (typeof window !== "undefined") {
  onConsentChange(() => {
    if (!initialized && consentAllowsErrorReporting()) {
      initSentry();
    }
  });
}

export function isSentryEnabled() {
  return initialized;
}

export function setSentryTag(key: string, value: string | number | boolean | null | undefined) {
  if (!initialized || value === null || value === undefined) return;
  try {
    Sentry.setTag(key, String(value));
  } catch {}
}

export function setSentryContext(name: string, ctx: Record<string, unknown>) {
  if (!initialized) return;
  try {
    Sentry.setContext(name, ctx);
  } catch {}
}

export function captureSentryError(err: unknown, ctx?: Record<string, unknown>) {
  if (!initialized) return;
  try {
    if (ctx) Sentry.setContext("extra", ctx);
    Sentry.captureException(err);
  } catch {}
}

export { Sentry };
